import type { FC, ReactNode } from 'react'
import { Box, Grid } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import type { PatientUser, Person } from '~/shared/types'
import type { Provider } from '~/entities/provider'
import { borderSx } from '~/shared/lib/borderSx'
import { mergeSx } from '~/shared/lib/mergeSx'
import { CommunicationThread } from '~/features/member-profile/communication'
import { Luci } from '~/features/member-profile/luci'
import PatientDetailNav from './PatientDetailNav/PatientDetailNav'
import type { NavMode } from './PatientDetailNav/navConfig'
import PatientDetailTopBar from './PatientDetailTopBar'
import { ExpandedModeContext, useExpandedModeControls } from './useExpandedMode'

// Takes `sx` explicitly rather than spreading leftover props onto the inner Box:
// the callers' border/width values are MUI system values, which v9 no longer reads
// off props, so spreading them styled nothing and littered the DOM instead.
const GridItem = ({
  children,
  xs,
  minWidth,
  sx = [],
}: {
  children: ReactNode
  xs?: number
  minWidth?: number | string
  sx?: SxProps<Theme>
}) => (
  <Grid component={Box} size={xs} sx={{ minWidth, position: 'relative' }}>
    <Box
      sx={mergeSx(
        {
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
        sx,
      )}
    >
      {children}
    </Box>
  </Grid>
)

interface PersonDetailLayoutProps {
  patient: PatientUser
  providers: Provider[]
  mode?: NavMode
  person?: Person
}

/** The member profile: top bar and tab nav on the left, the communication thread on the right. */
const PersonDetailLayout: FC<PersonDetailLayoutProps> = ({ patient, providers, mode, person }) => {
  const {
    LefthandGridContainerProps,
    toggleExpandedMode,
    resetExpandedMode,
    expandedModeIsActive,
  } = useExpandedModeControls()

  return (
    <ExpandedModeContext.Provider
      value={{ toggleExpandedMode, expandedModeIsActive, resetExpandedMode }}
    >
      <Grid
        container
        component={Box}
        wrap="nowrap"
        sx={{ position: 'absolute', width: '100%', height: '100%' }}
      >
        <GridItem {...LefthandGridContainerProps}>
          <PatientDetailTopBar
            patient={patient}
            providers={providers}
            actions={
              <Luci
                key={patient.person.id}
                personId={patient.person.id}
                patientId={mode === 'person' ? null : patient.id}
              />
            }
          />
          <PatientDetailNav patient={patient} mode={mode} person={person} />
        </GridItem>
        <GridItem minWidth={400} xs={5} sx={borderSx(['borderLeft'])}>
          <CommunicationThread personId={patient.person.id} />
        </GridItem>
      </Grid>
    </ExpandedModeContext.Provider>
  )
}

export default PersonDetailLayout
